import type { ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthModal } from './components/layout/AuthModal';
import { CartDrawer } from './components/layout/CartDrawer';
import { CookieConsent } from './components/layout/CookieConsent';
import { Footer } from './components/layout/Footer';
import { Header } from './components/layout/Header';
import { useAuth } from './hooks/useAuth';
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminOrdersPage } from './pages/AdminOrdersPage';
import { AdminProductEditPage } from './pages/AdminProductEditPage';
import { AdminProductsPage } from './pages/AdminProductsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { CatalogPage } from './pages/CatalogPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { ContactPage } from './pages/ContactPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { HomePage } from './pages/HomePage';
import { OrdersPage } from './pages/OrdersPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ProductPage } from './pages/ProductPage';
import { ProfilePage } from './pages/ProfilePage';
import { SubscriptionPage } from './pages/SubscriptionPage';
import { Providers } from './providers';

// An admin account is a separate internal tool, not a shopping session — bounce
// any non-/admin route straight to the admin panel rather than showing the shop.
function AdminRedirect({ children }: { children: ReactNode }) {
  const { data: user, isLoading } = useAuth();
  const location = useLocation();

  if (!isLoading && user?.role === 'admin' && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <Providers>
      <AppShell />
    </Providers>
  );
}

function AppShell() {
  const { data: user } = useAuth();
  const isAdmin = user?.role === 'admin';
  useGoogleAnalytics();

  return (
    <div className="flex min-h-screen flex-col">
      <Header />
      <main className="flex-1">
        <AdminRedirect>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/orders" element={<AdminOrdersPage />} />
            <Route path="/admin/products" element={<AdminProductsPage />} />
            <Route path="/admin/products/new" element={<AdminProductEditPage />} />
            <Route path="/admin/products/:id" element={<AdminProductEditPage />} />
            <Route path="/admin/users" element={<AdminUsersPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/favorites" element={<FavoritesPage />} />
            <Route path="/product/:id" element={<ProductPage />} />
            <Route path="/checkout" element={<CheckoutPage />} />
            <Route path="/orders" element={<OrdersPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/subscription" element={<SubscriptionPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </AdminRedirect>
      </main>
      {!isAdmin && <Footer />}
      <CartDrawer />
      <AuthModal />
      <CookieConsent />
    </div>
  );
}
